import React, { useState } from 'react';
import AdminLayout from '../AdminLayout';
import BroadcastEditor from './components/BroadcastEditor';
import ConfirmBroadcastModal from './components/ConfirmBroadcastModal';
import UserAutocomplete from './components/UserAutocomplete';
import { useGetRecipientCountQuery, useSendBroadcastMutation } from 'store/services/broadcast/broadcastApi';
import type {
  BroadcastSegment,
  BroadcastMessageType,
  BroadcastDeliveryChannel,
  SendBroadcastRequest,
} from 'types/interfaces/broadcast';
import MagicButton from 'components/MagicButton/MagicButton';
import { toast } from 'react-toastify';
import './BroadcastPage.css';

const SEGMENT_OPTIONS: { value: BroadcastSegment; label: string }[] = [
  { value: 'all', label: 'All Users' },
  { value: 'elite', label: 'Elite Users' },
  { value: 'free', label: 'Free Users' },
  { value: 'specific', label: 'Specific User' },
];

const MESSAGE_TYPE_OPTIONS: { value: BroadcastMessageType; label: string }[] = [
  { value: 'normal', label: 'Normal email' },
  { value: 'blog_announcement', label: 'Blog Announcement' },
];

const DELIVERY_CHANNEL_OPTIONS: { value: BroadcastDeliveryChannel; label: string }[] = [
  { value: 'both', label: 'Email + in-app' },
  { value: 'email', label: 'Email only' },
  { value: 'in_app', label: 'In-app only' },
];

const EMPTY_DOC = { type: 'doc', content: [{ type: 'paragraph' }] };

/** Add inline styles to <img> tags for email client compatibility */
const prepareHtmlForEmail = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  doc.querySelectorAll('img').forEach((img) => {
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    img.style.display = 'block';
  });
  return doc.body.innerHTML;
};

const isEditorEmpty = (html: string): boolean => !html || html === '<p></p>';

const BroadcastPage = () => {
  // Top-level routing fields
  const [messageType, setMessageType] = useState<BroadcastMessageType>('normal');
  const [deliveryChannel, setDeliveryChannel] = useState<BroadcastDeliveryChannel>('both');
  const [segment, setSegment] = useState<BroadcastSegment>('all');
  const [targetUsername, setTargetUsername] = useState<string>('');
  const [subject, setSubject] = useState('');

  // Normal email fields
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyJson, setBodyJson] = useState<any>(null);

  // Shared optional email fields (used by Normal collapsed section + Blog Announcement)
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [psContent, setPsContent] = useState('');
  const [showEmailOptions, setShowEmailOptions] = useState(false);

  // Blog Announcement fields
  const [blogTitle, setBlogTitle] = useState('');
  const [blogBodyHtml, setBlogBodyHtml] = useState('');
  const [blogBodyJson, setBlogBodyJson] = useState<any>(null);
  const [inAppHtml, setInAppHtml] = useState('');
  const [inAppJson, setInAppJson] = useState<any>(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const skipRecipientCount = segment === 'specific' && !targetUsername;
  const { data: recipientData, isLoading: isLoadingCount } = useGetRecipientCountQuery(
    { segment, messageType, targetUsername: targetUsername || undefined },
    { skip: skipRecipientCount },
  );
  const [sendBroadcast, { isLoading: isSending }] = useSendBroadcastMutation();

  const willEmail = deliveryChannel === 'email' || deliveryChannel === 'both';
  const willInApp = deliveryChannel === 'in_app' || deliveryChannel === 'both';

  const resetMessageFields = () => {
    setBodyHtml('');
    setBodyJson(EMPTY_DOC);
    setBlogTitle('');
    setBlogBodyHtml('');
    setBlogBodyJson(EMPTY_DOC);
    setInAppHtml('');
    setInAppJson(EMPTY_DOC);
    setCtaUrl('');
    setCtaText('');
    setPsContent('');
    setShowEmailOptions(false);
  };

  const handleMessageTypeChange = (next: BroadcastMessageType) => {
    setMessageType(next);
    resetMessageFields();
  };

  const canSend: boolean = (() => {
    if (subject.trim().length === 0) return false;
    if (segment === 'specific' && !targetUsername) return false;

    if (messageType === 'normal') {
      return !isEditorEmpty(bodyHtml);
    }

    // Blog Announcement
    if (!blogTitle.trim() || isEditorEmpty(blogBodyHtml)) {
      return false;
    }
    if (willEmail && (!ctaUrl.trim() || !ctaText.trim())) {
      return false;
    }
    if (willInApp && isEditorEmpty(inAppHtml)) {
      return false;
    }
    return true;
  })();

  const handleSend = () => {
    if (!canSend) return;
    setShowConfirmModal(true);
  };

  const buildPayload = (): SendBroadcastRequest => {
    const base: SendBroadcastRequest = {
      segment,
      messageType,
      deliveryChannel,
      subject: subject.trim(),
    };
    if (segment === 'specific') {
      base.targetUsername = targetUsername;
    }
    if (messageType === 'normal') {
      base.body = prepareHtmlForEmail(bodyHtml);
      if (ctaUrl.trim()) base.ctaUrl = ctaUrl.trim();
      if (ctaUrl.trim() && ctaText.trim()) base.ctaText = ctaText.trim();
      if (psContent.trim()) base.psContent = psContent.trim();
    } else {
      base.blogTitle = blogTitle.trim();
      base.blogBodyHtml = prepareHtmlForEmail(blogBodyHtml);
      if (willInApp) {
        base.inAppBody = prepareHtmlForEmail(inAppHtml);
      }
      if (willEmail) {
        base.ctaUrl = ctaUrl.trim();
        base.ctaText = ctaText.trim();
      }
      if (psContent.trim()) base.psContent = psContent.trim();
    }
    return base;
  };

  const handleConfirmSend = async () => {
    try {
      const result = await sendBroadcast(buildPayload()).unwrap();

      setShowConfirmModal(false);
      toast.success(
        `Broadcast sent to ${result.recipientCount.toLocaleString()} users (${result.emailCount.toLocaleString()} emails)`
      );

      // Reset everything to defaults
      setMessageType('normal');
      setDeliveryChannel('both');
      setSegment('all');
      setTargetUsername('');
      setSubject('');
      resetMessageFields();
    } catch (error: any) {
      setShowConfirmModal(false);
      const message = error?.data?.message || 'Failed to send broadcast. Please try again.';
      toast.error(message);
    }
  };

  const renderRecipientCount = () => {
    if (segment === 'specific' && !targetUsername) {
      return <span className="recipient-count-loading">Select a user…</span>;
    }
    if (isLoadingCount) {
      return <span className="recipient-count-loading">Loading recipient count…</span>;
    }
    if (!recipientData) {
      return null;
    }
    if (segment === 'specific' && recipientData.count === 0) {
      return (
        <span className="recipient-count-value">
          <strong>0</strong> users will receive this message (user not found or email unverified)
        </span>
      );
    }
    const willEmailHere = deliveryChannel === 'email' || deliveryChannel === 'both';
    return (
      <span className="recipient-count-value">
        <strong>{recipientData.count.toLocaleString()}</strong> users will receive this message
        {willEmailHere && (
          <> ({recipientData.emailCount.toLocaleString()} will also receive an email)</>
        )}
      </span>
    );
  };

  return (
    <AdminLayout title="Broadcast Message" currentPath="/admin/message">
      <div className="broadcast-page">
        <div className="broadcast-form">
          {/* Message Type */}
          <div className="broadcast-field">
            <label className="broadcast-label" htmlFor="broadcast-message-type">
              Message Type
            </label>
            <select
              id="broadcast-message-type"
              className="broadcast-select"
              value={messageType}
              onChange={(e) => handleMessageTypeChange(e.target.value as BroadcastMessageType)}
            >
              {MESSAGE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Delivery Channel */}
          <div className="broadcast-field">
            <label className="broadcast-label" htmlFor="broadcast-delivery-channel">
              Delivery Channel
            </label>
            <select
              id="broadcast-delivery-channel"
              className="broadcast-select"
              value={deliveryChannel}
              onChange={(e) => setDeliveryChannel(e.target.value as BroadcastDeliveryChannel)}
            >
              {DELIVERY_CHANNEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Segment */}
          <div className="broadcast-field">
            <label className="broadcast-label" htmlFor="broadcast-segment">
              Audience
            </label>
            <select
              id="broadcast-segment"
              className="broadcast-select"
              value={segment}
              onChange={(e) => {
                const next = e.target.value as BroadcastSegment;
                setSegment(next);
                if (next !== 'specific') {
                  setTargetUsername('');
                }
              }}
            >
              {SEGMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {segment === 'specific' && (
              <div className="broadcast-field" style={{ marginTop: 8 }}>
                <UserAutocomplete value={targetUsername} onChange={setTargetUsername} />
              </div>
            )}
          </div>

          {/* Recipient Count */}
          <div className="broadcast-recipient-count">{renderRecipientCount()}</div>

          {/* Subject */}
          <div className="broadcast-field">
            <label className="broadcast-label" htmlFor="broadcast-subject">
              Subject
            </label>
            <input
              id="broadcast-subject"
              type="text"
              className="broadcast-input"
              placeholder="Enter message subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
            <span className="broadcast-char-count">{subject.length}/200</span>
          </div>

          {messageType === 'normal' ? (
            <>
              {/* Body Editor */}
              <div className="broadcast-field">
                <label className="broadcast-label">Message Body</label>
                <BroadcastEditor content={bodyJson} onChange={(json, html) => { setBodyJson(json); setBodyHtml(html); }} />
              </div>

              {/* Email Options (collapsible) */}
              <div className="broadcast-email-options">
                <button
                  className="broadcast-email-options-toggle"
                  onClick={() => setShowEmailOptions(!showEmailOptions)}
                >
                  {showEmailOptions ? '▾' : '▸'} Email Options (optional)
                </button>

                {showEmailOptions && (
                  <div className="broadcast-email-options-content">
                    <div className="broadcast-field">
                      <label className="broadcast-label" htmlFor="broadcast-cta-url">
                        CTA Button URL
                      </label>
                      <input
                        id="broadcast-cta-url"
                        type="url"
                        className="broadcast-input"
                        placeholder="https://trademagic.gg/..."
                        value={ctaUrl}
                        onChange={(e) => setCtaUrl(e.target.value)}
                      />
                    </div>

                    {ctaUrl && (
                      <div className="broadcast-field">
                        <label className="broadcast-label" htmlFor="broadcast-cta-text">
                          CTA Button Text
                        </label>
                        <input
                          id="broadcast-cta-text"
                          type="text"
                          className="broadcast-input"
                          placeholder="Check It Out"
                          value={ctaText}
                          onChange={(e) => setCtaText(e.target.value)}
                        />
                      </div>
                    )}

                    <div className="broadcast-field">
                      <label className="broadcast-label" htmlFor="broadcast-ps">
                        P.S. Content
                      </label>
                      <input
                        id="broadcast-ps"
                        type="text"
                        className="broadcast-input"
                        placeholder="P.S. Don't forget to..."
                        value={psContent}
                        onChange={(e) => setPsContent(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Blog Title */}
              <div className="broadcast-field">
                <label className="broadcast-label" htmlFor="broadcast-blog-title">
                  Blog Title
                </label>
                <input
                  id="broadcast-blog-title"
                  type="text"
                  className="broadcast-input"
                  placeholder="The big headline above the body..."
                  value={blogTitle}
                  onChange={(e) => setBlogTitle(e.target.value)}
                  maxLength={200}
                />
              </div>

              {/* Email Body */}
              <div className="broadcast-field">
                <label className="broadcast-label">Email Body</label>
                <BroadcastEditor content={blogBodyJson} onChange={(json, html) => { setBlogBodyJson(json); setBlogBodyHtml(html); }} />
              </div>

              {/* CTA Text (required when email/both) */}
              <div className="broadcast-field">
                <label className="broadcast-label" htmlFor="broadcast-cta-text">
                  CTA Text {willEmail && <span className="broadcast-required">*</span>}
                </label>
                <input
                  id="broadcast-cta-text"
                  type="text"
                  className="broadcast-input"
                  placeholder="Enable SMS Alerts"
                  value={ctaText}
                  onChange={(e) => setCtaText(e.target.value)}
                />
              </div>

              {/* CTA URL (required when email/both) */}
              <div className="broadcast-field">
                <label className="broadcast-label" htmlFor="broadcast-cta-url">
                  CTA URL {willEmail && <span className="broadcast-required">*</span>}
                </label>
                <input
                  id="broadcast-cta-url"
                  type="url"
                  className="broadcast-input"
                  placeholder="https://trademagic.gg/articles/..."
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                />
              </div>

              {/* P.S. */}
              <div className="broadcast-field">
                <label className="broadcast-label" htmlFor="broadcast-ps">
                  P.S. (optional)
                </label>
                <input
                  id="broadcast-ps"
                  type="text"
                  className="broadcast-input"
                  placeholder="P.S. Don't forget to..."
                  value={psContent}
                  onChange={(e) => setPsContent(e.target.value)}
                />
              </div>

              {/* In-App Body (conditional) */}
              {willInApp && (
                <div className="broadcast-field">
                  <label className="broadcast-label">
                    In-App Body <span className="broadcast-required">*</span>
                  </label>
                  <span className="broadcast-helper-text">
                    What appears in the recipient's in-app inbox. Email recipients won't see this.
                  </span>
                  <BroadcastEditor content={inAppJson} onChange={(json, html) => { setInAppJson(json); setInAppHtml(html); }} />
                </div>
              )}
            </>
          )}

          {/* Send Button */}
          <div className="broadcast-actions">
            <MagicButton
              label="Send Broadcast"
              color="purple"
              onPress={handleSend}
              isDisabled={!canSend}
              fullWidth={false}
            />
          </div>
        </div>
      </div>

      <ConfirmBroadcastModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSend}
        isLoading={isSending}
        recipientCount={recipientData?.count ?? (segment === 'specific' && targetUsername ? 1 : 0)}
        emailCount={recipientData?.emailCount ?? 0}
        segment={segment}
        messageType={messageType}
        deliveryChannel={deliveryChannel}
        subject={subject.trim()}
        targetUsername={targetUsername || undefined}
      />
    </AdminLayout>
  );
};

export default BroadcastPage;
